import api from './api';

export type RouteStatus = 'Draft' | 'Published' | 'Archived';
export type RouteScopeType = 'City' | 'State' | 'Region' | 'Country';
export type RouteType = 'Collection' | 'Challenge';

export interface RouteShopInput {
  coffeeShopId: string;
  order: number;
  highlightText?: string;
  customImageUrl?: string;
}

export interface RouteInput {
  title: string;
  slug: string;
  shortDescription: string;
  coverImageUrl: string;
  cardBackgroundColor: string;
  featured: boolean;
  routeType: RouteType;
  status: RouteStatus;
  scopeType: RouteScopeType;
  scopeKey: string;
  startDate?: string | null;
  endDate?: string | null;
  sortOrder?: number | null;
  shops: RouteShopInput[];
}

export interface RouteListItem {
  id: string;
  title: string;
  slug: string;
  shortDescription: string;
  coverImageUrl: string;
  cardBackgroundColor: string;
  featured: boolean;
  routeType: RouteType;
  status: RouteStatus;
  scopeType: RouteScopeType;
  scopeKey: string;
  startDate?: string | null;
  endDate?: string | null;
  sortOrder?: number | null;
  coffeeShopCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RouteShop {
  id: string;
  coffeeShopId: string;
  name: string;
  description: string;
  logoUrl: string;
  coverImageUrl: string;
  address: string;
  cityName: string;
  stateName: string;
  order: number;
  highlightText?: string | null;
  customImageUrl?: string | null;
  isVisited: boolean;
}

export interface RouteDetail extends RouteListItem {
  visitedCoffeeShopCount: number;
  shops: RouteShop[];
}

export interface PaginatedRoutesResponse {
  routes: RouteListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export const routesService = {
  getRoutes: async (params?: {
    page?: number;
    pageSize?: number;
    scopeType?: string;
    scopeKey?: string;
    featured?: boolean;
    status?: string;
    search?: string;
  }): Promise<PaginatedRoutesResponse> => {
    const response = await api.get('/admin/routes', { params });
    return response.data;
  },

  getRouteById: async (id: string): Promise<RouteDetail> => {
    const response = await api.get(`/admin/routes/${id}`);
    return response.data;
  },

  createRoute: async (data: RouteInput): Promise<RouteDetail> => {
    const response = await api.post('/admin/routes', data);
    return response.data;
  },

  updateRoute: async (id: string, data: RouteInput): Promise<RouteDetail> => {
    const response = await api.put(`/admin/routes/${id}`, data);
    return response.data;
  },

  publishRoute: async (id: string): Promise<void> => {
    await api.patch(`/admin/routes/${id}/publish`);
  },

  archiveRoute: async (id: string): Promise<void> => {
    await api.patch(`/admin/routes/${id}/archive`);
  },

  deleteRoute: async (id: string): Promise<void> => {
    await api.delete(`/admin/routes/${id}`);
  },
};
